import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface RevokePremiumDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
  userEmail: string;
  onSuccess: () => void;
}

export function RevokePremiumDialog({
  open,
  onOpenChange,
  userId,
  userName,
  userEmail,
  onSuccess
}: RevokePremiumDialogProps) {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleRevoke = async () => {
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      // Call assign-user-products with empty array to remove all overrides
      const response = await fetch(
        'https://xuqkgeusasosxtcfhbwj.supabase.co/functions/v1/assign-user-products',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId,
            productIds: [], // Empty array removes all overrides
            source: 'admin_revoke'
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      toast({
        title: "Premium access revoked",
        description: `Successfully revoked admin overrides for ${userName}`
      });

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error revoking premium:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to revoke premium access"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Revoke Premium Access
          </DialogTitle>
          <DialogDescription>
            Revoke admin override for <strong>{userName}</strong> ({userEmail})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="rounded-lg bg-destructive/10 p-4 text-sm">
            <p className="font-medium text-destructive mb-2">
              Warning: This action will remove admin overrides
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>All admin-granted premium access will be removed</li>
              <li>Active subscriptions will NOT be affected</li>
              <li>User may lose access if they have no active subscription</li>
              <li>Trial enforcement will resume if applicable</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleRevoke}
            disabled={loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Revoke Access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
